import type { RegisteredRouter } from "@tanstack/react-router"
import posthog from "posthog-js"
import { type ReactNode, StrictMode } from "react"
import type { Root } from "react-dom/client"
import { notificationTaps } from "@/app/notification-taps"
import { trackApp } from "@/app/providers/analytics/app"
import {
	captureBootPageview,
	trackPageviews,
} from "@/app/providers/analytics/pageview"
import { DesktopBlockScreen } from "@/app/ui"
import { currentIdentity } from "@/entities/user"
import { captureError } from "@/shared/lib/diagnostics"
import { triggerImpact } from "@/shared/lib/platform"
import { initializeTelegramSDK } from "@/shared/lib/telegram"
import { startBackgroundTasks } from "./background"
import { disarmGuard } from "./guard"
import { entryRouteIds, splashHoldMs } from "./plan"
import { prepare } from "./prepare"
import { createReactRoot } from "./root"
import { bootFromSnapshot } from "./snapshot"
import { hasSeenSplash } from "./splash"
import { bootStore, transition } from "./store"
import { initSystemChrome } from "./system-chrome"
import { App } from "./ui/app"

interface Deps {
	createRouter(): RegisteredRouter
}

export class Boot {
	private readonly root: Root

	constructor(private readonly deps: Deps) {
		this.root = createReactRoot()
	}

	async run(): Promise<void> {
		const { platform, store, isDesktopBlocked } = prepare()
		// Outside React: StrictMode would fire the haptic twice in dev.
		triggerImpact("light")

		if (isDesktopBlocked) {
			this.renderDesktopBlock()
			return
		}

		const chrome = initSystemChrome(platform)
		const firstLaunch = !hasSeenSplash()
		const router = this.deps.createRouter()
		this.render(<App router={router} platform={platform} store={store} />)
		disarmGuard()

		try {
			transition("restoring")
			const restored = await bootFromSnapshot(router)
			transition("loading")
			void this.preloadRouteChunks(router, restored)
			await Promise.all([chrome, this.load(router)])
			await this.holdSplash(firstLaunch)
			transition("ready")
			captureBootPageview(router)
			this.report({ restored, firstLaunch })
			trackPageviews(router)
			trackApp()
			startBackgroundTasks(store)
		} catch (error) {
			captureError(error, { scope: "boot.run" })
			if (bootStore.state.phase !== "ready") transition("ready")
		}
	}

	private render(children: ReactNode): void {
		this.root.render(<StrictMode>{children}</StrictMode>)
	}

	// Boot snapshot → auth + home screen data (__root + _authenticated + home
	// loaders) → the screen the tapped push or Telegram button points at.
	private async load(router: RegisteredRouter): Promise<void> {
		try {
			await router.load()
		} catch (error) {
			captureError(error, { scope: "boot.load" })
		}
		await notificationTaps.attachRouter(router)
	}

	private async preloadRouteChunks(
		router: RegisteredRouter,
		restored: boolean,
	): Promise<void> {
		try {
			const hasSession =
				restored || (await currentIdentity().resolveId()) !== null
			await Promise.allSettled(
				entryRouteIds(hasSession).map((id) =>
					router.loadRouteChunk(router.routesById[id]),
				),
			)
		} catch (error) {
			captureError(error, { scope: "boot.preload" })
		}
	}

	// Counted from the document start, not from this call: the splash has been
	// up since the first frame of index.html.
	private holdSplash(firstLaunch: boolean): Promise<void> {
		const ms = splashHoldMs({ seen: !firstLaunch, now: performance.now() })
		return new Promise((resolve) => setTimeout(resolve, ms))
	}

	private report({
		restored,
		firstLaunch,
	}: {
		restored: boolean
		firstLaunch: boolean
	}): void {
		posthog.capture("boot_completed", {
			snapshot: restored ? "hit" : "miss",
			first_launch: firstLaunch,
			restore_ms: measure("boot:restore", "boot:restoring", "boot:loading"),
			load_ms: measure("boot:load", "boot:loading", "boot:ready"),
			total_ms: measure("boot:total", 0, "boot:ready"),
		})
	}

	// Telegram Desktop is unsupported: a stub screen instead of the app.
	private renderDesktopBlock(): void {
		// init() is required before ready(); without it Telegram keeps its own
		// loader over the stub.
		void initializeTelegramSDK().catch((error) => {
			captureError(error, { scope: "boot.desktopSdk" })
		})
		// The screen drops the splash itself: root.render() does not commit
		// synchronously.
		transition("ready")
		posthog.capture("$pageview")
		this.render(<DesktopBlockScreen />)
	}
}

function measure(name: string, start: string | number, end: string): number {
	return Math.round(performance.measure(name, { start, end }).duration)
}
